import { DateTime } from 'luxon';
import {
  type ComponentProps,
  createContext,
  type ReactNode,
  use,
  useState,
} from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Display, Hide, mq, StyleSheet } from 'react-native-unistyles';
import Box from '@/components/Unistyles/Box';
import List from '@/components/Unistyles/List';
import Text from '@/components/Unistyles/Text';
import {
  WelcomePrimaryAction,
  WelcomeSecondaryAction,
} from '@/components/Welcome/WelcomeUI';
import type {
  GeneratedGame,
  GeneratedGameTeam,
  GeneratedGameTeamSlotSelection,
} from './GeneratedGame.types';
import {
  sortGeneratedGamesByDatetime,
  swapGeneratedGameTeams,
  updateGeneratedGameTeam,
} from './GeneratedGame.utils';
import GeneratedGameCard from './GeneratedGameCard';
import GeneratedGameRosterPanel from './GeneratedGameRosterPanel';

type GeneratedScheduleFieldName = 'generatedScheduleGames' | 'games';

type GeneratedSchedulePreviewFormValues = {
  generatedScheduleGames: GeneratedGame[];
  games: GeneratedGame[];
};

type GeneratedSchedulePreviewState = {
  fieldName: GeneratedScheduleFieldName;
  gameIds: readonly string[];
  isTeamEditing: boolean;
  selectedRosterTeamId: string | null;
  selectedSlot: GeneratedGameTeamSlotSelection | null;
  teams: readonly GeneratedGameTeam[];
};

type GeneratedSchedulePreviewActions = {
  clearTeamSelection(): void;
  deleteGame(gameId: string): void;
  pressRosterTeam(teamId: string): void;
  pressTeamSlot(selection: GeneratedGameTeamSlotSelection): void;
  replaceGames(games: GeneratedGame[]): void;
  swapGameTeams(gameId: string): void;
  toggleTeamEditing(): void;
};

type GeneratedSchedulePreviewContextValue = {
  actions: GeneratedSchedulePreviewActions;
  state: GeneratedSchedulePreviewState;
};

type GeneratedSchedulePreviewProviderProps = {
  children: ReactNode;
  fieldName: GeneratedScheduleFieldName;
  teams: readonly GeneratedGameTeam[];
};

type GeneratedSchedulePreviewContentProps = {
  datetimeTestIDPrefix?: string;
  invalidMessage: string;
};

const GeneratedSchedulePreviewContext =
  createContext<GeneratedSchedulePreviewContextValue | null>(null);

function getGeneratedScheduleGames(
  values: GeneratedSchedulePreviewFormValues,
  fieldName: GeneratedScheduleFieldName,
) {
  switch (fieldName) {
    case 'games':
      return values.games;
    case 'generatedScheduleGames':
      return values.generatedScheduleGames;
  }
}

function hasInvalidGeneratedScheduleGame(games: readonly GeneratedGame[]) {
  return games.some((game) => !DateTime.fromISO(game.datetime ?? '').isValid);
}

export function useGeneratedSchedulePreview() {
  const context = use(GeneratedSchedulePreviewContext);

  if (!context) {
    throw new Error(
      'GeneratedSchedulePreview components must be rendered inside GeneratedSchedulePreview.Provider.',
    );
  }

  return context;
}

function useGeneratedScheduleGames() {
  const {
    state: { fieldName },
  } = useGeneratedSchedulePreview();
  const { control } = useFormContext<GeneratedSchedulePreviewFormValues>();

  return useWatch({
    control,
    name: fieldName,
  });
}

function GeneratedSchedulePreviewProvider({
  children,
  fieldName,
  teams,
}: GeneratedSchedulePreviewProviderProps) {
  const { getValues, setValue } =
    useFormContext<GeneratedSchedulePreviewFormValues>();
  const [gameIds, setGameIds] = useState(() =>
    getGeneratedScheduleGames(getValues(), fieldName).map((game) => game.id),
  );
  const [isTeamEditing, setIsTeamEditing] = useState(false);
  const [selectedRosterTeamId, setSelectedRosterTeamId] = useState<
    string | null
  >(null);
  const [selectedSlot, setSelectedSlot] =
    useState<GeneratedGameTeamSlotSelection | null>(null);

  const clearTeamSelection = () => {
    setSelectedRosterTeamId(null);
    setSelectedSlot(null);
  };

  const replaceGames = (nextGames: GeneratedGame[]) => {
    clearTeamSelection();
    setValue(fieldName, nextGames, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setGameIds(nextGames.map((game) => game.id));
  };

  const replaceGameTeam = (input: {
    gameId: string;
    selection: GeneratedGameTeamSlotSelection;
    teamId: string;
  }) => {
    const games = getGeneratedScheduleGames(getValues(), fieldName);
    const gameIndex = games.findIndex((game) => game.id === input.gameId);
    const game = games[gameIndex];

    if (!game) {
      return;
    }

    switch (fieldName) {
      case 'games':
        setValue(
          `games.${gameIndex}`,
          updateGeneratedGameTeam({
            game,
            slot: input.selection.slot,
            teamId: input.teamId,
          }),
          {
            shouldDirty: true,
            shouldValidate: true,
          },
        );
        return;
      case 'generatedScheduleGames':
        setValue(
          `generatedScheduleGames.${gameIndex}`,
          updateGeneratedGameTeam({
            game,
            slot: input.selection.slot,
            teamId: input.teamId,
          }),
          {
            shouldDirty: true,
            shouldValidate: true,
          },
        );
        return;
    }
  };

  const deleteGame = (gameId: string) => {
    const nextGames = getGeneratedScheduleGames(getValues(), fieldName).filter(
      (game) => game.id !== gameId,
    );

    replaceGames(nextGames);
  };

  const sortGamesAfterEditing = () => {
    const games = getGeneratedScheduleGames(getValues(), fieldName);
    const sortedGames = sortGeneratedGamesByDatetime(games);
    const nextGameIds = sortedGames.map((game) => game.id);
    const hasOrderChanged = games.some(
      (game, gameIndex) => game.id !== nextGameIds[gameIndex],
    );

    if (hasOrderChanged) {
      setValue(fieldName, sortedGames, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }

    setGameIds(nextGameIds);
  };

  const toggleTeamEditing = () => {
    clearTeamSelection();
    if (isTeamEditing) {
      sortGamesAfterEditing();
    }
    setIsTeamEditing(!isTeamEditing);
  };

  const pressTeamSlot = (selection: GeneratedGameTeamSlotSelection) => {
    if (selectedRosterTeamId) {
      replaceGameTeam({
        gameId: selection.gameId,
        selection,
        teamId: selectedRosterTeamId,
      });
      clearTeamSelection();
      return;
    }

    const isSameSelection =
      selectedSlot?.gameId === selection.gameId &&
      selectedSlot?.slot === selection.slot;

    setSelectedSlot(isSameSelection ? null : selection);
  };

  const pressRosterTeam = (teamId: string) => {
    if (selectedSlot) {
      replaceGameTeam({
        gameId: selectedSlot.gameId,
        selection: selectedSlot,
        teamId,
      });
      clearTeamSelection();
      return;
    }

    setSelectedRosterTeamId(selectedRosterTeamId === teamId ? null : teamId);
  };

  const swapGameTeams = () => {
    clearTeamSelection();
  };

  return (
    <GeneratedSchedulePreviewContext
      value={{
        actions: {
          clearTeamSelection,
          deleteGame,
          pressRosterTeam,
          pressTeamSlot,
          replaceGames,
          swapGameTeams,
          toggleTeamEditing,
        },
        state: {
          fieldName,
          gameIds,
          isTeamEditing,
          selectedRosterTeamId,
          selectedSlot,
          teams,
        },
      }}
    >
      {children}
    </GeneratedSchedulePreviewContext>
  );
}

function GeneratedScheduleGameListItemForGames({ gameId }: { gameId: string }) {
  const {
    state: { gameIds },
  } = useGeneratedSchedulePreview();
  const { control, setValue } =
    useFormContext<GeneratedSchedulePreviewFormValues>();
  const gameIndex = gameIds.indexOf(gameId);
  const watchedGameIndex = gameIndex < 0 ? 0 : gameIndex;
  const game = useWatch({
    control,
    name: `games.${watchedGameIndex}`,
  });

  if (gameIndex < 0 || !game || game.id !== gameId) {
    return null;
  }

  const setGame = (nextGame: GeneratedGame) => {
    setValue(`games.${gameIndex}`, nextGame, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  return (
    <GeneratedScheduleGameCard
      game={game}
      gameIndex={gameIndex}
      setGame={setGame}
    />
  );
}

function GeneratedScheduleGameListItemForWelcome({
  gameId,
}: {
  gameId: string;
}) {
  const {
    state: { gameIds },
  } = useGeneratedSchedulePreview();
  const { control, setValue } =
    useFormContext<GeneratedSchedulePreviewFormValues>();
  const gameIndex = gameIds.indexOf(gameId);
  const watchedGameIndex = gameIndex < 0 ? 0 : gameIndex;
  const game = useWatch({
    control,
    name: `generatedScheduleGames.${watchedGameIndex}`,
  });

  if (gameIndex < 0 || !game || game.id !== gameId) {
    return null;
  }

  const setGame = (nextGame: GeneratedGame) => {
    setValue(`generatedScheduleGames.${gameIndex}`, nextGame, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  return (
    <GeneratedScheduleGameCard
      game={game}
      gameIndex={gameIndex}
      setGame={setGame}
    />
  );
}

function GeneratedScheduleGameCard({
  game,
  gameIndex,
  setGame,
}: {
  game: GeneratedGame;
  gameIndex: number;
  setGame(game: GeneratedGame): void;
}) {
  const {
    actions,
    state: { isTeamEditing, selectedRosterTeamId, selectedSlot, teams },
  } = useGeneratedSchedulePreview();
  const listOptions = use(GeneratedScheduleListOptionsContext);

  if (!listOptions) {
    throw new Error(
      'GeneratedScheduleGameCard must be rendered in GeneratedScheduleGameList.',
    );
  }

  const onChangeDateTime = (value: string | null) => {
    if (value) {
      setGame({ ...game, datetime: value });
    }
  };

  const onChangeLocation = (value: string) => {
    setGame({ ...game, location: value || null });
  };

  const onChangeTitle = (value: string) => {
    setGame({ ...game, title: value });
  };

  const onSwapTeams = () => {
    setGame(swapGeneratedGameTeams(game));
    actions.swapGameTeams(game.id);
  };

  return (
    <GeneratedGameCard
      datetimeTestID={
        listOptions.datetimeTestIDPrefix
          ? `${listOptions.datetimeTestIDPrefix}-${gameIndex}-datetime`
          : undefined
      }
      game={game}
      isTeamEditing={isTeamEditing}
      onChangeDateTime={onChangeDateTime}
      onChangeLocation={onChangeLocation}
      onChangeTitle={onChangeTitle}
      onPressDelete={() => actions.deleteGame(game.id)}
      onPressSwapTeams={onSwapTeams}
      onPressTeamSlot={actions.pressTeamSlot}
      selectedRosterTeamId={selectedRosterTeamId}
      selectedSlot={selectedSlot}
      teams={teams}
    />
  );
}

type GeneratedScheduleListOptions = {
  datetimeTestIDPrefix?: string;
};

const GeneratedScheduleListOptionsContext =
  createContext<GeneratedScheduleListOptions | null>(null);

function GeneratedScheduleGameListItem({ gameId }: { gameId: string }) {
  const {
    state: { fieldName },
  } = useGeneratedSchedulePreview();

  switch (fieldName) {
    case 'games':
      return <GeneratedScheduleGameListItemForGames gameId={gameId} />;
    case 'generatedScheduleGames':
      return <GeneratedScheduleGameListItemForWelcome gameId={gameId} />;
  }
}

function GeneratedScheduleGameList({
  datetimeTestIDPrefix,
}: {
  datetimeTestIDPrefix?: string;
}) {
  const {
    state: { gameIds },
  } = useGeneratedSchedulePreview();
  const renderItem = ({ item }: { item: string }) => (
    <GeneratedScheduleGameListItem gameId={item} />
  );

  return (
    <GeneratedScheduleListOptionsContext value={{ datetimeTestIDPrefix }}>
      <List
        contentContainerStyle={styles.listContent}
        data={gameIds}
        keyExtractor={(gameId) => gameId}
        maintainVisibleContentPosition={false}
        recycleItems={false}
        renderItem={renderItem}
        style={styles.list}
      />
    </GeneratedScheduleListOptionsContext>
  );
}

function GeneratedScheduleRoster({
  presentation,
}: {
  presentation?: 'panel' | 'tray';
}) {
  const {
    actions,
    state: { selectedRosterTeamId, selectedSlot, teams },
  } = useGeneratedSchedulePreview();
  const games = useGeneratedScheduleGames();

  return (
    <GeneratedGameRosterPanel
      games={games}
      onPressTeam={actions.pressRosterTeam}
      presentation={presentation}
      selectedRosterTeamId={selectedRosterTeamId}
      selectedSlot={selectedSlot}
      teams={teams}
    />
  );
}

function GeneratedScheduleValidationMessage({ message }: { message: string }) {
  const games = useGeneratedScheduleGames();

  return hasInvalidGeneratedScheduleGame(games) ? (
    <Text variant="error" center selectable>
      {message}
    </Text>
  ) : null;
}

function GeneratedSchedulePreviewContent({
  datetimeTestIDPrefix,
  invalidMessage,
}: GeneratedSchedulePreviewContentProps) {
  const {
    state: { isTeamEditing },
  } = useGeneratedSchedulePreview();

  styles.useVariants({
    editing: isTeamEditing,
  });

  return (
    <Box w="full" space="sm" style={styles.reviewContent}>
      <Box w="full" style={styles.reviewLayout}>
        {isTeamEditing ? (
          <Display mq={mq.only.width('xl')}>
            <Box style={styles.rosterColumn}>
              <GeneratedScheduleRoster />
            </Box>
          </Display>
        ) : null}
        <Box flex style={styles.eventColumn}>
          <GeneratedScheduleGameList
            datetimeTestIDPrefix={datetimeTestIDPrefix}
          />
        </Box>
      </Box>
      <GeneratedScheduleValidationMessage message={invalidMessage} />
    </Box>
  );
}

function GeneratedScheduleRosterTray() {
  const {
    state: { isTeamEditing },
  } = useGeneratedSchedulePreview();

  return isTeamEditing ? (
    <Hide mq={mq.only.width('xl')}>
      <GeneratedScheduleRoster presentation="tray" />
    </Hide>
  ) : null;
}

function GeneratedScheduleEditAction() {
  const {
    actions,
    state: { isTeamEditing },
  } = useGeneratedSchedulePreview();

  return (
    <WelcomeSecondaryAction
      label={isTeamEditing ? 'Done editing' : 'Edit'}
      icon={isTeamEditing ? 'checkmark-outline' : 'create-outline'}
      onPress={actions.toggleTeamEditing}
    />
  );
}

function GeneratedSchedulePrimaryAction({
  disabled,
  ...props
}: Omit<ComponentProps<typeof WelcomePrimaryAction>, 'disabled'> & {
  disabled?: boolean;
  label: string;
}) {
  const games = useGeneratedScheduleGames();
  const hasInvalidGame = hasInvalidGeneratedScheduleGame(games);

  return (
    <WelcomePrimaryAction
      disabled={disabled || games.length === 0 || hasInvalidGame}
      {...props}
    />
  );
}

const GeneratedSchedulePreview = {
  Content: GeneratedSchedulePreviewContent,
  EditAction: GeneratedScheduleEditAction,
  PrimaryAction: GeneratedSchedulePrimaryAction,
  Provider: GeneratedSchedulePreviewProvider,
  RosterTray: GeneratedScheduleRosterTray,
};

export default GeneratedSchedulePreview;

const styles = StyleSheet.create((theme) => ({
  eventColumn: {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
  },
  list: {
    backgroundColor: theme.colors.background,
    flex: 1,
    minHeight: 0,
  },
  listContent: {
    backgroundColor: theme.colors.background,
    paddingBottom: theme.space('sm'),
  },
  reviewContent: {
    flex: 1,
    minHeight: 0,
    width: '100%',
  },
  reviewLayout: {
    alignItems: 'stretch',
    flexDirection: 'column',
    gap: theme.space('sm'),
    flex: 1,
    minHeight: 0,
    width: '100%',
    variants: {
      editing: {
        true: {
          flexDirection: {
            base: 'column',
            xl: 'row',
          },
        },
      },
    },
  },
  rosterColumn: {
    flexBasis: theme.size(82),
    flexGrow: 0,
    flexShrink: 0,
    minHeight: 0,
    width: theme.size(82),
  },
}));
